import { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { t } from "i18next";
import { Folder } from "../../../../interfaces/folder";
import { createFolder, updateFolder } from "../../../../services/folder";

interface FolderModalProps {
    show: boolean;
    onClose: () => void;
    onSave: () => void;
    folder: Folder | null;
    parentId: number | null;
}

export function FolderModal({ show, onClose, onSave, folder, parentId }: FolderModalProps) {
    const [folderName, setFolderName] = useState('');
    const [folderDescription, setFolderDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (folder) {
            setFolderName(folder.name);
            setFolderDescription(folder.description || '');
        } else {
            setFolderName('');
            setFolderDescription('');
        }
        setError('');
    }, [folder, show]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!folderName.trim()) {
            setError(t('Folder name is required'));
            return;
        }

        setLoading(true);

        const promise = folder && folder.id > 0
            ? updateFolder({
                id: folder.id,
                name: folderName,
                description: folderDescription.trim() || null
            })
            : createFolder({
                name: folderName,
                description: folderDescription.trim() || undefined,
                parent_id: folder?.parent_id || parentId
            });

        promise
            .then(() => {
                onSave();
            })
            .catch(err => {
                setError(err.response?.data?.message || t('Error saving folder'));
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <Modal show={show} onHide={onClose}>
            <Modal.Header closeButton>
                <Modal.Title>
                    {folder && folder.id > 0 ? t('Edit Folder') : t('New Folder')}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && (
                        <div className="alert alert-danger">{error}</div>
                    )}
                    <Form.Group className="mb-3">
                        <Form.Label>{t('Folder Name')}</Form.Label>
                        <Form.Control
                            type="text"
                            value={folderName}
                            onChange={(e) => setFolderName(e.target.value)}
                            placeholder={t('Enter folder name')}
                            autoFocus
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('Description')} ({t('optional')})</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={folderDescription}
                            onChange={(e) => setFolderDescription(e.target.value)}
                            placeholder={t('Enter folder description')}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose} disabled={loading}>
                        {t('Cancel')}
                    </Button>
                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? t('Saving...') : t('Save')}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
